import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { ForgotPasswordDialog } from '@/components/auth/ForgotPasswordDialog';
import { SessionConflictDialog } from '@/components/auth/SessionConflictDialog';
import { Truck, Mail, Lock, Package, ArrowLeft, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

function translateAuthError(message: string): string {
  if (message.includes('Invalid login credentials')) return 'E-mail ou senha incorretos.';
  if (message.toLowerCase().includes('banned')) return 'Perfil suspenso ou inativado. Contate o administrador.'; // §I.146
  if (message.includes('Email not confirmed')) return 'E-mail não confirmado. Verifique sua caixa de entrada.';
  if (message.includes('rate limit')) return 'Muitas tentativas. Aguarde um momento.';
  return message;
}

interface DriverAuthProps {
  createSession: (forceLogout?: boolean) => Promise<boolean>;
  sessionConflict: { existingIp: string; lastActivity: string } | null;
  forceLogoutOtherDevice: () => Promise<boolean>;
  cancelConflict: () => void;
}

export default function DriverAuth({ createSession, sessionConflict, forceLogoutOtherDevice, cancelConflict }: DriverAuthProps) {
  const { user, signIn, signOut, loading: authLoading } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // ✅ FIXED: Sequential login flow — signIn → createSession → navigate
  // Removed the problematic useEffect that caused phantom login
  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    try {
      // Step 1: Sign in and wait for profile to load
      const { error } = await signIn(email, password);
      
      if (error) {
        setFormError(translateAuthError(error.message || 'Verifique suas credenciais e tente novamente.'));
        setIsSubmitting(false);
        return;
      }

      // Step 2: Create driver session
      setIsCreatingSession(true);
      const sessionCreated = await createSession(false);
      setIsCreatingSession(false);

      if (sessionCreated) {
        localStorage.setItem('gonx-driver-mode', 'true');
        navigate('/driver', { replace: true });
      }
      // If session wasn't created (conflict), the SessionConflictDialog will show
    } catch (err) {
      setFormError('Erro inesperado. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForceLogout = async () => {
    setIsCreatingSession(true);
    const success = await forceLogoutOtherDevice();
    setIsCreatingSession(false);
    
    if (success) {
      toast({
        title: 'Sessão criada',
        description: 'A sessão anterior foi encerrada. Você está conectado.',
      });
      localStorage.setItem('gonx-driver-mode', 'true');
      navigate('/driver', { replace: true });
    } else {
      toast({
        title: 'Erro',
        description: 'Não foi possível encerrar a sessão anterior.',
        variant: 'destructive'
      });
    }
  };

  const handleCancelConflict = async () => {
    cancelConflict();
    await signOut();
    toast({
      title: 'Login cancelado',
      description: 'Você pode tentar novamente mais tarde.',
    });
  };

  // Show loading while checking auth status
  // DRIVER-LOGIN-ERRO (I.160): enquanto a própria tela confere a senha, o formulário fica ("Entrando..." no botão),
  // para a mensagem de erro e o e-mail digitado não sumirem
  if ((authLoading && !isSubmitting) || isCreatingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-orange-500/20 p-4 rounded-xl">
            <Truck className="h-12 w-12 text-orange-500 animate-pulse" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-blue-200/70 text-sm">
            {isCreatingSession ? 'Criando sessão...' : 'Verificando...'}
          </p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4 relative overflow-y-auto">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => { localStorage.removeItem('gonx-driver-mode'); navigate('/'); }} 
        className="absolute top-4 left-4 text-blue-200/70 hover:text-white hover:bg-white/10 z-20"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Voltar para o site
      </Button>

      {/* Decorative truck icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Truck className="absolute top-10 left-10 h-16 w-16 text-blue-500/10 rotate-12" />
        <Truck className="absolute top-20 right-20 h-24 w-24 text-orange-500/10 -rotate-12" />
        <Truck className="absolute bottom-20 left-20 h-20 w-20 text-blue-500/10 rotate-6" />
        <Truck className="absolute bottom-10 right-10 h-16 w-16 text-orange-500/10 -rotate-6" />
        <Package className="absolute top-1/4 right-10 h-12 w-12 text-blue-500/10" />
        <Package className="absolute bottom-1/4 left-10 h-12 w-12 text-orange-500/10" />
        
        {/* Animated truck */}
        <div className="absolute bottom-32 w-full">
          <Truck className="h-8 w-8 text-orange-500/20 animate-[slide_15s_linear_infinite]" />
        </div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4 cursor-pointer" onClick={() => navigate('/')}>
            <div className="bg-orange-500/20 p-3 rounded-xl">
              <Truck className="h-10 w-10 text-orange-500" />
            </div>
            <img 
              src="/gonx-logo.png" 
              alt="GOnX Logo" 
              className="h-16 md:h-24 w-auto"
            />
          </div>
          
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Truck className="h-4 w-4" />
            MODO ROTA
          </div>
          
          <h1 className="text-2xl font-bold text-white">GOnX Driver</h1>
        </div>

        {/* Auth Card */}
        <Card className="shadow-2xl border-blue-500/20 bg-slate-800/90 backdrop-blur-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl text-white flex items-center justify-center gap-2">
              <Truck className="h-5 w-5 text-orange-500" />
              Entrar no Sistema
            </CardTitle>
            <CardDescription className="text-blue-200/70">
              Use suas credenciais de acesso
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* ✅ Inline error alert */}
            {formError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-blue-100">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-blue-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10 bg-slate-700/50 border-blue-500/30 text-white placeholder:text-slate-400 focus:border-orange-500"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-blue-100">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-blue-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 bg-slate-700/50 border-blue-500/30 text-white placeholder:text-slate-400 focus:border-orange-500"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="link"
                    className="text-sm p-0 h-auto text-blue-300 hover:text-orange-400"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Esqueci minha senha
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 text-base gap-2"
                disabled={isSubmitting}
              >
                <Truck className="h-5 w-5" />
                {isSubmitting ? 'Entrando...' : 'Entrar no Sistema'}
              </Button>
            </form>

            <p className="text-xs text-blue-200/50 text-center mt-6">
              Motoristas são cadastrados pelo administrador.
              <br />
              Entre em contato se precisar de acesso.
            </p>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-blue-200/50 flex items-center justify-center gap-2">
          <Truck className="h-4 w-4" />
          <p>© 2024 GOnX Driver - Sistema de Entregas</p>
          <Truck className="h-4 w-4" />
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={showForgotPassword} 
        onOpenChange={setShowForgotPassword}
      />

      {/* Session Conflict Dialog */}
      <SessionConflictDialog
        open={!!sessionConflict}
        existingIp={sessionConflict?.existingIp || ''}
        lastActivity={sessionConflict?.lastActivity || ''}
        onForceLogout={handleForceLogout}
        onCancel={handleCancelConflict}
        isLoading={isCreatingSession}
      />

      {/* CSS for animation */}
      <style>{`
        @keyframes slide {
          0% { transform: translateX(-100px); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
      `}</style>
    </div>
  );
}